import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { EInvoiceDoc } from 'src/model/e-invoice..model';
import { BusinessService } from 'src/services/business.service';
import { EInvoiceService } from 'src/services/e-invoice.service';
import { MatTableController } from '../mat-table-controller';
import { SharedService } from '../Shared/shared-service.service';
import { BusinessStore } from 'src/model/business/business-store';

interface CombinedEInvoiceDoc {
  combined: EInvoiceDoc;
  docs: EInvoiceDoc[];
  expanded: boolean;
}

@Component({
  selector: 'app-e-invoice-doc-list',
  imports: [FormsModule, MatTableModule, MatCheckboxModule, MatSortModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatChipsModule],
  templateUrl: './e-invoice-doc-list.component.html',
  styleUrl: './e-invoice-doc-list.component.scss',
  providers: [MatTableController]
})
export class EInvoiceDocListComponent {
  private eInvoiceService = inject(EInvoiceService);
  private businessService = inject(BusinessService);
  private sharedService = inject(SharedService);
  private businessStore = inject(BusinessStore);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  public table = inject(MatTableController<EInvoiceDoc>);


  licenseBusinessIdMap = this.businessStore.licenseBusinessIdMap;
  // licenseBusinessMap = signal<Map<number, number>>(new Map());
  eInvoiceDocs = signal<EInvoiceDoc[]>([]);
  grouped = computed(() => this.sharedService.groupByMultiple(this.eInvoiceDocs(), ['CreatedOn', 'BusinessName']));
  groupedDocs = computed(() => this.groupEInvoiceDocs(this.eInvoiceDocs()));
  expanded = signal<number[]>([]);
  showFilter = signal<boolean>(false);
  displayedColumns = computed(() => ['Expand', ...this.table.columnDefs(), 'Actions']);

  duration = signal<number>(3);

  constructor() {
    this.table.config.set([
      // { columnDef: 'Id', header: 'ID', property: 'Id' },
      { columnDef: 'Business', header: 'Business', property: 'BusinessName' },
      { columnDef: 'TotalAmount', header: 'Total Amount', property: 'TotalInterest', type: 'currency', cssClass: {header: 'align-end', cell: 'text-end' } },
      { columnDef: 'IssuanceDate', header: 'Issuance Date', property: 'IssuanceDate', type: 'date', format: 'dd MMM yyyy' },
      { columnDef: 'CreatedOn', header: 'Created On', property: 'CreatedOn', type: 'date', format: 'dd MMM yyyy, hh:mm:ss a' },
      { columnDef: 'Status', header: 'Status', calculated: (d) => d.SubmittedOn === undefined ? 'Draft' : 'Submitted' },
    ]);

    effect(() => {
      const g = this.grouped();
      this.addExpanded(g);
    })
  }

  ngOnInit(): void {
    this.getEInvoiceDocs();
    // this.getAllLicenses();
  }

  async getEInvoiceDocs() {
    const e = await this.eInvoiceService.getAllEInvoiceDocs();
    this.eInvoiceDocs.set(e.sort((a, b) => b.IssuanceDate.getTime() - a.IssuanceDate.getTime()));
    // const grouped = this.groupEInvoiceDocs(e);
    // this.table.rawData.set(this.groupedDocs().map(d => ({ headerRow: d, items: d.docs })));
    this.table.rawData.set([{items: this.groupedDocs()}]);
  }

  // async getAllLicenses() {
  //   const list = await this.businessService.getAllBusinessLicenses();
  //   const map = new Map(list.map(l => [l.Id, l.BusinessId]));
  //   this.licenseBusinessIdMap.set(map);
  // }

  groupEInvoiceDocs(docs: EInvoiceDoc[]) {
    // const grouped = this.sharedService.groupByMultiple(docs, ['CreatedOn', 'BusinessName']);
    const result: EInvoiceDoc[] = [];
    Object.values(this.grouped()).forEach(g => {
      if (g.length > 1) {
        const sum = g.reduce((a, c) => a + c.TotalInterest, 0);
        const item = Object.assign(new EInvoiceDoc(), g[0]);
        item.EInvoiceNumber = undefined;
        item.TotalInterest = sum;

        result.push(item);
      }
      result.push(...g);
    })
    return result;
  }

  addEInvoiceDoc(businessId?: number) {
    this.router.navigate(['/form-j-selection'], businessId ? {
      relativeTo: this.route,
      queryParams: {
        branchId: businessId,
      },
      queryParamsHandling: 'merge'
    } : {});
  }

  isGrouped(index, item): boolean {
    return item.EInvoiceNumber === undefined;
  }

  addExpanded(grouped: Record<string, EInvoiceDoc[]>) {
    const e = Object.values(grouped).filter(g => g.length === 1).map(d => d[0].Id);
    this.expanded.set(e);
  }

  isCombined(doc: EInvoiceDoc) {
    return this.grouped()[[doc.CreatedOn, doc.BusinessName].join('|')].length > 1;
  }

  openEInvoiceDoc(licenseId: number, docDate: Date) {
    this.router.navigate(['/form-j-selection'], {
      relativeTo: this.route,
      queryParams: {
        branchId: this.licenseBusinessIdMap().get(licenseId),
        currentYear: docDate.getFullYear(),
        currentMonth: docDate.getMonth(),
      },
      queryParamsHandling: 'merge'
    });
  }

  toggleCollapse(doc: EInvoiceDoc) {
    const ids = this.grouped()[[doc.CreatedOn, doc.BusinessName].join('|')]?.map(d => d.Id);
    this.expanded.update(current => {
      const exists = current.includes(ids[0]);
      return exists ? current.filter(id => !ids.includes(id)) : [...current, ...ids];
    });
  }

  toggleFilter() {
    const s = this.showFilter();
    this.showFilter.set(!s);
  }
}
